import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HistoryIcon from "@mui/icons-material/History";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import FlagIcon from "@mui/icons-material/Flag";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { get, post } from "src/utils/networkUtils";

/**
 * Season replay ("time travel") for QA. Pick a gameweek from the ingested
 * historical season; the whole timeline shifts so that gameweek is about to
 * lock. QA builds teams, then kicks off / completes the gameweek and every
 * number can be checked against the real last-season result.
 */
export default function SeasonReplayPanel({ canRun }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gameweek, setGameweek] = useState(1);
  const [minutesUntilLock, setMinutesUntilLock] = useState(20);
  const [windowMinutes, setWindowMinutes] = useState(6);
  const [compressMinutes, setCompressMinutes] = useState(10);
  const [busy, setBusy] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get("/admin/match-simulator/replay/status");
      setStatus(r?.data ?? null);
      setError(null);
    } catch (err) {
      setError(
        err?.response?.data?.message ?? err?.message ?? "Failed to load replay status"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const call = async (action, path, body) => {
    setBusy(action);
    setError(null);
    setLastResult(null);
    try {
      const r = await post(path, body);
      setLastResult({ action, ...(r?.data ?? {}) });
      await loadStatus();
    } catch (err) {
      setError(
        err?.response?.data?.message ?? err?.message ?? `Failed to ${action}`
      );
    } finally {
      setBusy(null);
    }
  };

  const gw = status?.gameweeks?.find((g) => g.number === Number(gameweek));

  const renderSteps = (result) =>
    result?.steps?.length ? (
      <List dense disablePadding>
        {result.steps.map((step, i) => (
          <ListItem key={i} disableGutters alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
              {step.ok ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : (
                <CancelIcon color="error" fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={step.name}
              secondary={step.detail}
              primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </ListItem>
        ))}
      </List>
    ) : null;

  const gwStatusColor = (s) =>
    ({ finalised: "success", live: "warning", locked: "secondary", open: "info" }[s] ??
    "default");

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Season replay (time travel)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Put the whole game at a point in the ingested{" "}
                  {status?.tournament ?? "season"} so QA can validate real
                  logic against last season&apos;s actual results. Pick a
                  gameweek, jump the timeline to just before it locks, then
                  build teams, kick off, and complete - all through the real
                  scoring pipeline and the production gameweek lifecycle:
                  open (make changes) &rarr; locked at the deadline (pending
                  transfers, captaincy and boosters apply; the NEXT gameweek
                  opens) &rarr; live (points climb) &rarr; finalised (settled
                  and frozen). Kick off / Complete jump those boundaries on
                  demand; Auto-play runs one gameweek on the clock; Compressed
                  runs the rest of the season unattended, one gameweek per
                  window. {status?.matchesWithStats ?? 0} matches have replay
                  stats.
                </Typography>
              </Box>

              {error && <Alert severity="error">{error}</Alert>}
              {loading && <CircularProgress size={22} />}

              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Gameweek"
                    value={gameweek}
                    onChange={(e) => setGameweek(e.target.value)}
                    inputProps={{ min: 1, max: 38 }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Minutes until lock"
                    value={minutesUntilLock}
                    onChange={(e) => setMinutesUntilLock(e.target.value)}
                    inputProps={{ min: 1, max: 1440 }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Tooltip
                      title={
                        canRun
                          ? ""
                          : "Enable MATCH_SIMULATOR_APPLY_ENABLED to use replay"
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          startIcon={
                            busy === "time-travel" ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <HistoryIcon />
                            )
                          }
                          onClick={() =>
                            call(
                              "time-travel",
                              "/admin/match-simulator/replay/setup",
                              {
                                gameweek: Number(gameweek),
                                minutesUntilLock: Number(minutesUntilLock),
                              }
                            )
                          }
                          disabled={!canRun || Boolean(busy)}
                        >
                          Time travel to GW {gameweek}
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={
                        busy === "kickoff" ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <PlayCircleIcon />
                        )
                      }
                      onClick={() =>
                        call("kickoff", "/admin/match-simulator/replay/kickoff", {
                          gameweek: Number(gameweek),
                        })
                      }
                      disabled={!canRun || Boolean(busy)}
                    >
                      Kick off GW {gameweek}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={
                        busy === "complete" ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <FlagIcon />
                        )
                      }
                      onClick={() =>
                        call("complete", "/admin/match-simulator/replay/complete", {
                          gameweek: Number(gameweek),
                        })
                      }
                      disabled={!canRun || Boolean(busy)}
                    >
                      Complete GW {gameweek}
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={
                        busy === "autoplay" ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <SmartToyIcon />
                        )
                      }
                      onClick={() =>
                        call("autoplay", "/admin/match-simulator/replay/autoplay", {
                          gameweek: Number(gameweek),
                          buildMinutes: Number(minutesUntilLock),
                          windowMinutes: Number(windowMinutes),
                        })
                      }
                      disabled={!canRun || Boolean(busy)}
                    >
                      Auto-play GW {gameweek}
                    </Button>
                    <TextField
                      size="small"
                      type="number"
                      label="Play window (min)"
                      value={windowMinutes}
                      onChange={(e) => setWindowMinutes(e.target.value)}
                      sx={{ width: 140 }}
                      disabled={!canRun || Boolean(busy)}
                    />
                    {status?.autoplay ? (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() =>
                          call(
                            "stop-autoplay",
                            "/admin/match-simulator/replay/autoplay/stop"
                          )
                        }
                        disabled={Boolean(busy)}
                      >
                        Stop auto-play (GW {status.autoplay.gameweek})
                      </Button>
                    ) : null}
                    <Tooltip title="Time travel to the chosen gameweek, then run it and every later gameweek unattended - one full lifecycle (lock, kick off, points climb, settle, next opens) per window. Great for watching the whole season flow.">
                      <span>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={
                            busy === "compressed" ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <SmartToyIcon />
                            )
                          }
                          onClick={() =>
                            call(
                              "compressed",
                              "/admin/match-simulator/replay/setup",
                              {
                                gameweek: Number(gameweek),
                                minutesUntilLock: Number(minutesUntilLock),
                                compressMinutes: Number(compressMinutes),
                              }
                            )
                          }
                          disabled={!canRun || Boolean(busy)}
                        >
                          Compressed from GW {gameweek}
                        </Button>
                      </span>
                    </Tooltip>
                    <TextField
                      size="small"
                      type="number"
                      label="Min per gameweek"
                      value={compressMinutes}
                      onChange={(e) => setCompressMinutes(e.target.value)}
                      sx={{ width: 150 }}
                      inputProps={{ min: 2, max: 1440 }}
                      disabled={!canRun || Boolean(busy)}
                    />
                    {status?.compressed ? (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() =>
                          call(
                            "stop-compressed",
                            "/admin/match-simulator/replay/compressed/stop"
                          )
                        }
                        disabled={Boolean(busy)}
                      >
                        Stop compressed run (from GW{" "}
                        {status.compressed.fromGameweek},{" "}
                        {status.compressed.compressMinutes} min/GW)
                      </Button>
                    ) : null}
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={
                        busy === "reset" ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <RestartAltIcon />
                        )
                      }
                      onClick={() => {
                        if (
                          window.confirm(
                            "Reset the whole season back to finished - all matches played, real scores, points restored, every gameweek finalised. This undoes the current time-travel."
                          )
                        ) {
                          call("reset", "/admin/match-simulator/replay/reset");
                        }
                      }}
                      disabled={!canRun || Boolean(busy)}
                    >
                      Reset season
                    </Button>
                  </Stack>
                </Grid>
              </Grid>

              {gw && (
                <Alert severity="info" icon={<SportsSoccerIcon />}>
                  GW {gw.number}: {gw.matches} matches ({gw.played} played,{" "}
                  {gw.playing} live, {gw.fixtures} upcoming) - status{" "}
                  <strong>{gw.status}</strong>
                  {gw.deadline_at
                    ? `, locks ${new Date(gw.deadline_at).toLocaleString()}`
                    : ""}
                </Alert>
              )}

              {lastResult && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {lastResult.action === "time-travel"
                        ? "Time travel"
                        : lastResult.action === "compressed"
                          ? "Compressed season"
                          : lastResult.action === "kickoff"
                            ? "Kick off"
                            : "Complete"}{" "}
                      result
                      {lastResult.locksAt
                        ? ` - locks at ${new Date(lastResult.locksAt).toLocaleString()}`
                        : ""}
                    </Typography>
                    {renderSteps(lastResult)}
                  </Box>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Gameweeks
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>GW</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Matches</TableCell>
                    <TableCell align="center">Played</TableCell>
                    <TableCell align="center">Live</TableCell>
                    <TableCell align="center">Upcoming</TableCell>
                    <TableCell>Locks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(status?.gameweeks ?? []).map((g) => (
                    <TableRow
                      key={g.number}
                      hover
                      selected={g.number === Number(gameweek)}
                      onClick={() => setGameweek(g.number)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{g.number}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={g.status}
                          color={gwStatusColor(g.status)}
                        />
                      </TableCell>
                      <TableCell align="center">{g.matches}</TableCell>
                      <TableCell align="center">{g.played}</TableCell>
                      <TableCell align="center">{g.playing}</TableCell>
                      <TableCell align="center">{g.fixtures}</TableCell>
                      <TableCell>
                        {g.deadline_at
                          ? new Date(g.deadline_at).toLocaleString()
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
